import type Highcharts from 'highcharts';

import type { DChartInterface } from 'webapp-types';
import { shadeColor } from 'webapp/utils/color';

import type { HighchartsChartWithCustomPlotlines } from 'webapp/d-chart/renderer/base';
import FunnelOrPipelineBaseRenderer from 'webapp/d-chart/renderer/funnel-or-pipeline-base';
import type {
	AxisWithPlotLinesAndBands,
	PointWithDataLabel,
} from 'webapp/d-chart/renderer/funnel-or-pipeline-base';

export default class PipelineRenderer extends FunnelOrPipelineBaseRenderer {
	axisForOverallConversion = 'xAxis' as const;

	override get visualization() {
		return super.visualization;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;

		let options = super.baseOptions;

		options = this.mergeOptions(options, this.commmonBaseOptions, {
			chart: {
				type: 'streamgraph',
				events: {
					click: function (this: Highcharts.Chart, e: Highcharts.PointerEventObject) {
						const xPos = this.xAxis[0]?.toValue(e.chartX);
						if (typeof xPos === 'number') {
							that.sendEventForBlockContentClickFromPosition(xPos);
						}

						if (this.hoverPoint) {
							const { dbxPoint, graphic } = this
								.hoverPoint as DChartInterface.ChartHoverPoint;
							that.onPointClick(dbxPoint, e, graphic?.element);
						}
					},
				},
			},
			yAxis: [
				{
					visible: false,
					startOnTick: false,
					endOnTick: false,
					maxPadding: 0.05,
					minPadding: 0,
				},
			],
		});

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		const m = this.dChart.sizeModifier;
		let options = super.sizeOptions;

		options = this.mergeOptions(options, {
			chart: {
				marginRight: 0,
				marginLeft: 0,
				spacingBottom: m / 2, // to not cut off bottom labels
			},
			xAxis: {
				title: {
					margin: m,
				},
			},
		});

		return options;
	}

	protected preparePlotLinesAndBands() {
		const plotBands: DChartInterface.PlotBandOptions[] = [];

		this.series.forEach((series) => {
			if (series.isMain) {
				series.points.forEach((point) => {
					// adding plot bands - just once
					const plotBandStartIndex = point.index * this.pointsIncludingFakePerActualData;
					if (!plotBands.find((item) => item.from === plotBandStartIndex)) {
						plotBands.push({
							dbxPoint: point,
							from: plotBandStartIndex,
							to: plotBandStartIndex + this.pointsIncludingFakePerActualData,
							label: {
								useHTML: true,
								align: 'left',
								text: this.formatPlotBandText(point),
							},
						});
					}
				});
			}
		});

		return {
			plotBands,
			plotLines: [],
		};
	}

	protected getSeriesGradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			customColorClassMap = this.dChart.customColorClassMap,
			themeId = themeData.themeId,
			colors = themeData.colors || [],
			primaryColors = themeData.visualizationConfig.order || [],
			color1 = primaryColors[0],
			color2 = primaryColors[1],
			blockId = this.visualization.block.id;

		const defs: DChartInterface.Def[] = [
			{
				tagName: 'linearGradient',
				id: `gradient-pipeline-compare-series-${themeId}`,
				x1: 0,
				y1: 1,
				x2: 0,
				y2: 0,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color1 ? shadeColor(colors[color1]!, 30) : '',
						'stop-opacity': 0.4,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': color2 ? shadeColor(colors[color2]!, 30) : '',
						'stop-opacity': 0.4,
					},
				],
			},
		];

		const addDefForParams = (idPostfix: string, color: string) => {
			const id = `gradient-pipeline-main-series-${idPostfix}`;
			defs.push({
				tagName: 'linearGradient',
				id,
				x1: 0,
				y1: 1,
				x2: 0,
				y2: 0,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': shadeColor(color, 30),
					},
				],
			});
		};

		primaryColors.forEach((colorIndex: number) => {
			addDefForParams(`${themeId}-${colorIndex}`, colors[colorIndex]!);
		});

		Object.entries(customColorClassMap).forEach((entry) => {
			const [hex, customClass] = entry;
			addDefForParams(`${blockId}-${customClass}`, hex);
		});

		return defs;
	}

	protected updateDynamicElementsOnRender(hChart: HighchartsChartWithCustomPlotlines): void {
		if (!hChart.series.length) {
			return;
		}

		const m = this.dChart.sizeModifier,
			xAxis = hChart.xAxis[0],
			plotLinesAndBands = (xAxis as AxisWithPlotLinesAndBands).plotLinesAndBands,
			plotBands = plotLinesAndBands.filter((item) => !!item.label);

		let plotBandLabelMaxHeight = 0;

		// Calculating offset of percent bubble (dataLabel), and max height for steps 'title'
		{
			const dataItemsWithDataLabels: PointWithDataLabel[] = [];
			hChart.series.forEach((s) => {
				dataItemsWithDataLabels.push(
					...(s.data as PointWithDataLabel[]).filter((item) => !!item.dataLabel),
				);
			});

			plotBands.forEach((plotBand) => {
				dataItemsWithDataLabels.forEach((dataItem) => {
					if (plotBand.options.from === dataItem.x) {
						// get dataLabel on the beginning of plot band
						const dataLabel = dataItem.dataLabel;

						// calculating dataLabel position to make sure its centered vertically in series area.
						if (dataLabel) {
							const centerPosY =
									(dataItem.yBottom + dataItem.plotY) / 2 - dataLabel.height / 2, // get center y position: (bottom of graph area + top of graph area) / 2 - bubbleHeight / 2;
								newDataLabelPosition = {
									y: centerPosY,
									x:
										(plotBand.label!.alignAttr.x as number) +
										m * (m < 7 ? 0.6 : 1.2), // adding m because of padding (check .highcharts-plot-band-label .info)
								};

							if (this.animateBubblePath && this.dChart.isAnimated) {
								dataLabel.animate(newDataLabelPosition, {
									duration: this.animationDuration,
								});
							} else {
								dataLabel.attr(newDataLabelPosition); // to prevent floating bubbles from initial position (when we change step conversion or switching show percent)
							}
						}
					}
				});

				// finding highest plot band label to apply negative height as top chart margin - also to calculate height of custom plot line.
				plotBandLabelMaxHeight = Math.max(
					plotBandLabelMaxHeight,
					plotBand.label!.element.offsetHeight,
				);
			});
		}

		this.animateBubblePath = false;

		// drawing custom plot lines
		{
			const plotLineHeight = hChart.plotHeight + plotBandLabelMaxHeight, // include plot band label max height (steps 'title')
				plotLineWidth = m * 0.05;

			let plotBandWidth = 0;
			if (!hChart.customPlotLines) {
				hChart.customPlotLines = [];
			}

			const usedPlotLines: Highcharts.SVGElement[] = [];
			plotBands.forEach((plotBand, index) => {
				if (index < plotBands.length - 1) {
					// not rendering line for last plot band
					const pointX =
							(plotBand.options.from as number) +
							this.pointsIncludingFakePerActualData,
						posX = xAxis!.toPixels(pointX, false);

					let customPlotLine = hChart.customPlotLines[index];

					if (customPlotLine) {
						const customPlotLineData: Highcharts.SVGAttributes = {
							x: posX,
							height: plotLineHeight,
							width: plotLineWidth,
						};

						if (this.dChart.isAnimated) {
							customPlotLine.animate(customPlotLineData, {
								duration: this.animationDuration,
							});
						} else {
							customPlotLine.attr(customPlotLineData);
						}
					} else {
						hChart.customPlotLines[index] = customPlotLine = hChart.renderer
							.rect(posX, 0, plotLineWidth, plotLineHeight)
							.attr({
								zIndex: 4,
							})
							.addClass('highcharts-dbx-custom-plot-line')
							.add();
					}
					usedPlotLines.push(customPlotLine);

					if (index === 0) {
						plotBandWidth = posX - xAxis!.toPixels(0, false);
					}
				}

				// setting label container width to prevent text overflow
				plotBand.label!.element.style.width = `${plotBandWidth}px`;
			});

			// remove unused plotlines
			hChart.customPlotLines.forEach((pl) => {
				if (!usedPlotLines.includes(pl)) {
					pl.destroy();
				}
			});
			hChart.customPlotLines = usedPlotLines;
		}

		// make some space for steps 'title'
		{
			if (plotBandLabelMaxHeight !== hChart.userOptions.chart?.marginTop) {
				xAxis!.options.plotBands?.forEach((item) => {
					item.label!.y = -plotBandLabelMaxHeight;
				});

				hChart.update({
					chart: {
						marginTop: plotBandLabelMaxHeight,
					},
				});
			}
		}
	}
}
